import { Component,  Input, EventEmitter, Output, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { sleepForMS } from '../../helpers/helpers';
import { InputDialogReturnData } from '../wizards/input.dialog.component';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { DialogPositionNumbers } from '../../../../globals/store/dialog.selector.data';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'dialog-wrapper',
    imports: [ CommonModule, MatDialogModule, IconComponent, MatButtonModule],
    template: `
<div #dialog class="joi-dialog" [ngStyle]="{ maxHeight: absoluteMaxHeight }">
  <div mat-dialog-title class="wrapper title"
    [ngStyle]="{background: backgroundColor || 'none', minHeight: +titleHeight + 'px', height: +titleHeight + 'px' }"
    [ngClass]="{normal: !boldTitle, smallTitle: smallTitle, bold: boldTitle, noBackground: !backgroundColor }">
    <icon class="back" *ngIf="showBack" icon="ARROW_BACK" (click)="back.emit()"></icon>
    {{ dialogTitle }}
    <icon class="close" *ngIf="showClose" icon="close" (click)="close.emit()"></icon>
  </div>
  <div #content class="wrapper-content" [ngStyle]="{ maxHeight: maxHeight, minHeight: minHeight ? (minHeight + 'px !important') : 'unset' }" mat-dialog-content [ngClass]="{ giveMeTwenty: giveMeTwenty, giveMeTen: giveMeTen, tiny: tiny, fixedHeight: dontResize, fitContent: fitContent }">
    <div #contentChildren class="content-children">
      <ng-content></ng-content>
    </div>
  </div>
  <div *ngIf="!hideBottom" class="menu-buttons" mat-dialog-actions [ngStyle]="{height: +titleHeight + 10 + 'px'}" >
    <button matButton class="delete" *ngIf="deleteLeft" (click)="delete.emit($event)"><icon icon="delete24"></icon></button>
    <button matButton *ngIf="cancelLeft" [disabled]="cancelDisabled"  (click)="cancel.emit()">{{ cancelLabel }}</button>
    <button matButton *ngIf="midLeftLabel" [disabled]="disableAll && !enableMidRight" (click)="onMidLeft.emit()">{{ midLeftLabel }}</button>
    <div style="flex:1;"></div>
    <button matButton class="delete" *ngIf="deleteRight" (click)="delete.emit($event)"><icon icon="delete24"></icon></button>
    <button matButton *ngIf="cancelRight" (click)="cancel.emit()">{{ cancelLabel }}</button>
    <button matButton *ngIf="midRightLabel" [disabled]="disableAll && !enableMidRight" (click)="onMidRight.emit()">{{ midRightLabel }}</button>
    <button matButton="filled" color="primary" [disabled]="disabled" (click)="submit.emit()">{{ successLabel }}</button>
  </div>
</div>
`,
    styleUrls: ['./dialog-wrapper.scss'],
    standalone: true,
})
export class DialogWrapperComponent implements OnInit, AfterViewInit {

  @Input() disabled = true
  @Input() cancelDisabled = false
  @Input() disableAll = false
  @Input() hideBottom = false
  @Input() successLabel = 'SAVE'
  @Input() cancelLabel = 'CANCEL'
  @Input() dialogTitle = ''
  @Input() titleHeight = 60
  @Input() backgroundColor
  @Input() cancelLeft = false
  @Input() cancelRight = false
  @Input() smallTitle = false
  @Input() boldTitle = false
  @Input() deleteRight = false
  @Input() deleteLeft = false
  @Input() showClose = false
  @Input() showBack = false
  @Input() dontResize = false
  @Input() fitContent = false
  @Input() giveMeTwenty = false
  @Input() giveMeTen = false
  @Input() dontGiveExtraSpacing = false
  @Input() isMobile = false
  @Input() tiny = false
  @Input() minHeight: number
  @Input() maxHeight = 'unset'
  @Input() data: DialogWrapperData

  @Output() back: EventEmitter<any> = new EventEmitter()
  @Output() close: EventEmitter<any> = new EventEmitter()
  @Output() cancel: EventEmitter<any> = new EventEmitter()
  @Output() submit: EventEmitter<any> = new EventEmitter()
  @Output() delete: EventEmitter<any> = new EventEmitter()


  @Input() midLeftLabel?: string
  @Input() midRightLabel?: string
  @Input() enableMidRight = false
  @Input() enableMidLeft = false
  @Output() onMidLeft: EventEmitter<any> = new EventEmitter()
  @Output() onMidRight: EventEmitter<any> = new EventEmitter()

  @ViewChild('content', { static: true }) content: ElementRef;
  @ViewChild('dialog', { static: true }) dialog: ElementRef;
  @ViewChild('contentChildren', { static: true }) contentChildren: ElementRef;
  @Input() absoluteMaxHeight = 'calc(100vh - 40px)'


  ngOnInit() {
    if (!this.data || this.dontResize) return
    
    const { position, event, dialogRef } = this.data
    if (position) {
      const updatePosition = {}
      Object.entries(position).forEach(([ pos, val ]) => updatePosition[pos] = `${val}px`)
      // console.log({ updatePosition })
      dialogRef.updatePosition(updatePosition)
    }
  }

  fitChildren(extraHeight = 0) { // for navigating between "different dialogs" and shrinking to fit, or growing to fit afterward
    const childrenHeight = this.contentChildren.nativeElement.scrollHeight +  extraHeight + ((this.giveMeTwenty || this.isMobile) && !this.dontGiveExtraSpacing ? 40 : 0)
    const newHeight = Math.min(childrenHeight, this.calculateHeights().newHeight);

    // console.log({ childrenHeight, newHeight, giveMeTwenty: this.giveMeTwenty, dontGiveExtraSpacing: this.dontGiveExtraSpacing })
    this.content.nativeElement.style.height = `${newHeight}px`
  }

  async fitChildrenWithDelay(ms, extraHeight = 0) {
    // console.log("Should be fitting...")
    await sleepForMS(ms)
    this.fitChildren(extraHeight)
  }


  calculateHeights() {
    const contentEl = this.content.nativeElement
    const { top, bottom: supposedBottom } = contentEl.getBoundingClientRect()
    const calculatedHeight = supposedBottom - top + 5
    const oldHeight = +contentEl.style.height.split("px")[0]
    const bottom = supposedBottom + oldHeight - calculatedHeight
    const screenHeight = window.innerHeight
    const bottomOverflow = screenHeight - bottom - this.titleHeight - 10
    const newHeight = bottom - top + bottomOverflow - 4 + ((this.giveMeTwenty || this.isMobile) && !this.dontGiveExtraSpacing ? 40 : 0)
    const overflow = bottomOverflow < 0

    // console.log({ calculatedHeight, newHeight, overflow, top, supposedBottom, oldHeight, bottom, screenHeight, bottomOverflow })
    return { calculatedHeight, newHeight, overflow }
  }

  shrinkToFit() {
    const { newHeight, overflow } = this.calculateHeights()
    if (overflow) {
      setTimeout(() => {
        this.content.nativeElement.style.height = `${newHeight}px` 
        this.scrollToTop()  
      }, 200)
    } else this.scrollToTop()
  }

  scrollToTop() {
    setTimeout(() => this.content.nativeElement.scrollTop = 0, 200)
  }

  jumpToBottom() {
    setTimeout(() => {
      const el = this.content.nativeElement
      el.scrollTop = el.scrollHeight - el.clientHeight
    }, 200)
  }

  ngAfterViewInit() {
    if (this.dontResize) return
    const contentEl = this.content.nativeElement
    setTimeout(() => {
      const calculatedHeight = this.calculateHeights().calculatedHeight
      contentEl.style.height = `${calculatedHeight}px`
      this.shrinkToFit()
    }, 200)
  }

}

export interface DialogWrapperData {
  event?: any
  position?: DialogPositionNumbers
  dialogRef: MatDialogRef<any> | { close: (data: InputDialogReturnData) => null, updatePosition?: any }
}

export const defaultDialogData: () => DialogWrapperData = () => ({
  position: null,
  dialogRef: { close: (data: InputDialogReturnData) => null, updatePosition: null },
})